// Mounts the Vexo analytics script once, gated on:
//   1. The GrowthBook flag `FEATURE_TOGGLE.VexoAnalytics`
//   2. `APP_ENVIRONMENT` resolves to a known Vexo project key (skips local dev)
//   3. Not running inside Cypress (window.Cypress unset)
//
// Idempotent: re-runs (e.g. after the GrowthBook flag flips on at hydrate
// time) won't append a second script tag.
//
// Also registers unload-time listeners (visibilitychange / pagehide) that
// call `flushVexoEvents()` so batched events get drained before the tab
// closes. `beforeunload` is intentionally not used — it's unreliable on
// mobile Safari + back-forward cache, and `pagehide` covers the same path.
//
// SEE: docs/tenant-admin-vexo-analytics-plan.md §1 + §9 edge case #12.

import { useEffect } from 'react';
import { useFeatureIsOn } from '@growthbook/growthbook-react';

import { FEATURE_TOGGLE } from '@/constants/featuretoggle';
import { flushVexoEvents } from './events';

const SCRIPT_SRC = 'https://www.vexo.co/analytics.js';

// If the Vexo endpoint is over its plan limit it can hang (onload never
// fires) or serve an error body. After this long we stop waiting and drop
// the dead <script> so the app is never left depending on a stalled tag.
const LOAD_TIMEOUT_MS = 10000;

// Safe fallback / kill switch.
//
// Vexo's analytics.js auto-instruments the page (it patches fetch/XHR and
// does session capture). When the project blows past its event/session
// quota the loaded SDK can misbehave badly enough to take the whole app
// down — and the only reliable protection is to NOT load it. The existing
// GrowthBook `VexoAnalytics` flag is the planned off-switch, but it needs a
// flag round-trip (and GrowthBook itself to be healthy). These two checks
// give an instant, dependency-free escape hatch:
//
//   1. Build-time env `DISABLE_VEXO=true` — bakes Vexo off for a deploy.
//   2. Runtime `localStorage['vexo:disabled'] = '1'` — ops/support can kill
//      Vexo in a single browser by setting the key and reloading, with no
//      deploy and no flag change. Wrapped in try/catch because localStorage
//      access throws in some privacy/sandboxed contexts.
function isVexoKilled(): boolean {
  if (process.env.DISABLE_VEXO === 'true') return true;
  try {
    return window.localStorage?.getItem('vexo:disabled') === '1';
  } catch {
    return false;
  }
}

// Per-environment Vexo project keys. `APP_ENVIRONMENT` is set at build
// time (see webpack/CI config) — `NODE_ENV` is `production` for both prod
// and QA builds, so we key off the app env instead.
//
// Returns `null` for environments where Vexo should not run (local dev,
// unset env, unknown values). The bootstrap short-circuits on null.
//
// Note on init pattern: the script is loaded plain (no query param / data
// attribute); we call `window.vexo.init(key)` after `script.onload` if
// the SDK exposes it. If Vexo's CDN actually routes purely by registered
// domain — which it does today for tenant-admin per Vexo support — the
// init call is a no-op (the SDK either doesn't expose `init` or accepts
// the call without effect). Keeping the env-keyed call in place future-
// proofs us against switching to multi-project domain registration.
// Keys match the canonical uppercase APP_ENVIRONMENT values the build
// system injects (see `src/constants/env.ts`: `PROD`, `QA`, etc.). The
// lookup normalizes to uppercase so a misconfigured env that ships
// lowercase still resolves.
const VEXO_API_KEYS: Record<string, string> = {
  PROD: '9b3043da-96cf-4260-b9b9-7ca2abca6351',
  STAGING: 'c742499c-2c50-47b8-b030-c71ca956a4ee',
  QA: 'c742499c-2c50-47b8-b030-c71ca956a4ee',
  PREVIEW: 'c742499c-2c50-47b8-b030-c71ca956a4ee',
};

function resolveVexoApiKey(): string | null {
  const env = process.env.APP_ENVIRONMENT?.toUpperCase();
  if (!env) return null;
  return VEXO_API_KEYS[env] ?? null;
}

export function useVexoBootstrap(): void {
  const enabled = useFeatureIsOn(FEATURE_TOGGLE.VexoAnalytics);

  useEffect(() => {
    if (!enabled) return;
    if (typeof window === 'undefined') return;
    // Edge #12: Cypress runs would otherwise pollute the dashboard.
    if (window.Cypress) return;
    // Safe fallback: never load Vexo when killed via env or localStorage.
    if (isVexoKilled()) return;
    const apiKey = resolveVexoApiKey();
    // No key for this env (e.g. local dev) — skip the SDK entirely so
    // events don't bleed into a project they don't belong to.
    if (!apiKey) return;

    let loadTimer: ReturnType<typeof setTimeout> | undefined;

    // Whole block is wrapped: appending a third-party script must never be
    // able to throw into React's render/commit and blank the app.
    try {
      if (!document.querySelector(`script[src="${SCRIPT_SRC}"]`)) {
        const s = document.createElement('script');
        s.src = SCRIPT_SRC;
        s.defer = true;
        // Best-effort init via the post-load API if the SDK exposes it.
        // Wrapped in try/catch — tracking must never break the app.
        s.onload = () => {
          if (loadTimer) clearTimeout(loadTimer);
          try {
            window.vexo?.init?.(apiKey);
          } catch {
            /* swallow */
          }
        };
        // Over-limit / blocked endpoints can 4xx/5xx the script. Drop the
        // dead tag so a later mount can retry, and never throw.
        s.onerror = () => {
          if (loadTimer) clearTimeout(loadTimer);
          // eslint-disable-next-line no-console
          console.warn('[vexo] analytics.js failed to load — skipping');
          s.remove();
        };
        // If the endpoint hangs (onload/onerror never fire), give up and
        // drop the pending tag so nothing waits on a stalled load.
        loadTimer = setTimeout(() => {
          if (!window.vexo) {
            // eslint-disable-next-line no-console
            console.warn('[vexo] analytics.js load timed out — skipping');
            s.remove();
          }
        }, LOAD_TIMEOUT_MS);
        document.head.appendChild(s);
      }
    } catch {
      /* swallow — analytics must never break app startup */
    }

    // Drain queued events whenever the tab becomes hidden — covers tab
    // close, navigation away, mobile background, and the back-forward
    // cache path. `pagehide` is the explicit unload signal; we listen
    // to both because some browsers fire one but not the other reliably.
    const onHidden = () => {
      if (document.visibilityState === 'hidden') flushVexoEvents();
    };
    const onPageHide = () => flushVexoEvents();

    document.addEventListener('visibilitychange', onHidden);
    window.addEventListener('pagehide', onPageHide);

    return () => {
      if (loadTimer) clearTimeout(loadTimer);
      document.removeEventListener('visibilitychange', onHidden);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, [enabled]);
}
